import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { motion, useDragControls, useMotionValue } from 'framer-motion';
import { createPortal } from 'react-dom';
import { PointerEvent, useLayoutEffect, useRef, useState } from 'react';
import ReferenceContent from './reference-content.tsx';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
}

const ReferenceModal = ({ isOpen, onClose }: IProps) => {
  const [expanded, setExpanded] = useState(false);
  const controls = useDragControls();
  const constraintsRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(16);

  const dragHandler = (e: PointerEvent<HTMLElement>) => {
    if (e.currentTarget.id === 'reference-drag-header') controls.start(e);
  };

  useLayoutEffect(() => {
    if (!isOpen) return;

    const updatePanelPosition = () => {
      if (!panelRef.current) return;

      const rect = panelRef.current.getBoundingClientRect();
      const maxX = Math.max(16, window.innerWidth - rect.width - 16);
      const maxY = Math.max(16, window.innerHeight - rect.height - 16);
      const hasBeenPositioned = x.get() !== 0 || y.get() !== 16;

      if (!hasBeenPositioned) {
        x.set(maxX);
        y.set(16);
        return;
      }

      x.set(Math.min(Math.max(16, x.get()), maxX));
      y.set(Math.min(Math.max(16, y.get()), maxY));
    };

    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);

    return () => {
      window.removeEventListener('resize', updatePanelPosition);
    };
  }, [expanded, isOpen, x, y]);

  if (!isOpen) return null;

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={constraintsRef}
      className="pointer-events-none fixed inset-0 z-[9999]"
      onClick={onClose}
    >
      <motion.div
        ref={panelRef}
        drag
        dragListener={false}
        dragControls={controls}
        dragMomentum={false}
        dragConstraints={constraintsRef}
        dragElastic={0}
        style={{ x, y }}
        className={`pointer-events-auto absolute flex flex-col overflow-hidden rounded-lg bg-white shadow-xl ${
          expanded
            ? 'top-0 left-0 max-h-[calc(100vh-4rem)] w-[calc(100vw-2rem)] max-w-[min(88rem,calc(100vw-2rem))]'
            : 'top-0 left-0 max-h-[90vh] w-[min(34rem,calc(100vw-4rem))] max-w-[calc(100vw-4rem)]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          id="reference-drag-header"
          className="flex cursor-move items-center justify-between bg-[#1f1f1f] px-5 py-4 text-white select-none"
          onPointerDown={dragHandler}
        >
          <span className="text-base font-semibold">Reference Sheet</span>

          <div className="flex flex-1 justify-center">
            <div
              className="grid cursor-grab grid-cols-3 gap-[6px] rounded-md p-3"
              style={{ backgroundColor: 'transparent' }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor =
                  'rgba(255,255,255,0.08)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = 'transparent')
              }
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[6px] w-[6px] rounded-full bg-white"
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 text-gray-200">
            <button
              type="button"
              onClick={() => setExpanded((current) => !current)}
              onPointerDown={(e) => e.stopPropagation()}
              className="flex h-12 w-12 items-center justify-center rounded-[18px] border-[2.5px] border-white/90 hover:bg-white/10"
              title={expanded ? 'Collapse window' : 'Expand window'}
            >
              {expanded ? (
                <ArrowsPointingInIcon className="h-5 w-5 scale-90" />
              ) : (
                <ArrowsPointingOutIcon className="h-5 w-5 scale-90" />
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              onPointerDown={(e) => e.stopPropagation()}
              className="cursor-pointer rounded p-1 hover:bg-white/10"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-5">
          <ReferenceContent />
        </div>
      </motion.div>
    </div>,
    document.body,
  );
};

export default ReferenceModal;
